/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { formatApiError } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Image as ImageIcon, ArrowUp, ArrowDown, Eye, EyeOff, BookOpen } from "lucide-react";
import { BP_CATEGORIES, BP_LEVELS, getCategoryMeta, getLevelMeta } from "@/lib/bestPracticesMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_IMAGE_BYTES = 1_500_000; // ~1.5 MB raw → ~2 MB base64

const EMPTY = {
  titre: "", categorie: "sante", image: "", resume: "", contenu: "",
  a_retenir: "", erreurs_a_eviter: "", conseils_pratiques: "",
  niveau: "debutant", mots_cles: [], ordre_affichage: 0, actif: true,
};

export default function AdminBestPractices() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = () => {
    setBusy(true);
    axios.get(`${API}/best-practices?include_inactive=true&page_size=200`, { withCredentials: true })
      .then((res) => {
        // Backend returns { items, total, has_more } for paginated endpoint
        const data = res.data;
        setItems(Array.isArray(data) ? data : (data?.items || []));
      })
      .catch(() => setItems([]))
      .finally(() => setBusy(false));
  };

  useEffect(() => { refresh(); }, []);

  const openCreate = () => { setEditing({ ...EMPTY }); setOpenDialog(true); };
  const openEdit = async (id) => {
    try {
      const res = await axios.get(`${API}/best-practices/${id}`, { withCredentials: true });
      setEditing(res.data);
      setOpenDialog(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleSave = async (form) => {
    try {
      if (form.id) {
        await axios.put(`${API}/admin/best-practices/${form.id}`, form, { withCredentials: true });
        toast.success("Fiche mise à jour");
      } else {
        await axios.post(`${API}/admin/best-practices`, form, { withCredentials: true });
        toast.success("Fiche créée");
      }
      setOpenDialog(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette fiche définitivement ?")) return;
    try {
      await axios.delete(`${API}/admin/best-practices/${id}`, { withCredentials: true });
      toast.success("Fiche supprimée");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API}/admin/best-practices/${item.id}`, { actif: !item.actif }, { withCredentials: true });
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const move = async (item, dir) => {
    const newOrder = item.ordre_affichage + dir;
    try {
      await axios.put(`${API}/admin/best-practices/${item.id}`, { ordre_affichage: newOrder }, { withCredentials: true });
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <DashboardShell allowedRoles={["owner"]}>
      <header className="mb-8 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">
            <BookOpen size={14} /> Administration
          </div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">
            Fiches de bonnes pratiques
          </h1>
          <p className="mt-1 text-[#5C6B5C]">
            Gérez la bibliothèque accessible aux clients Pro.
          </p>
        </div>
        <Button data-testid="bp-create-btn" onClick={openCreate} className="bg-[#E05A3D] hover:bg-[#C84B31]">
          <Plus size={16} className="me-1.5" /> Nouvelle fiche
        </Button>
      </header>

      {busy ? (
        <p className="text-[#5C6B5C] py-12 text-center">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E2E4DF] bg-white p-10 text-center">
          <BookOpen size={36} className="mx-auto text-[#A8AFA1] mb-3" />
          <p className="font-bold text-[#1C3A27]">Aucune fiche pour l'instant</p>
          <p className="text-sm text-[#5C6B5C] mt-1">Créez votre première fiche pour démarrer la bibliothèque.</p>
        </div>
      ) : (
        <div className="rounded-2xl bg-white border border-[#E2E4DF] overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAF6] text-[#5C6B5C] text-xs uppercase tracking-wider font-bold">
              <tr>
                <th className="px-4 py-3 text-start">Ordre</th>
                <th className="px-4 py-3 text-start">Titre</th>
                <th className="px-4 py-3 text-start">Catégorie</th>
                <th className="px-4 py-3 text-start">Niveau</th>
                <th className="px-4 py-3 text-start">Statut</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const cat = getCategoryMeta(it.categorie);
                const lvl = getLevelMeta(it.niveau);
                return (
                  <tr key={it.id} data-testid={`bp-admin-row-${it.id}`} className="border-t border-[#E2E4DF]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => move(it, -1)} className="p-1 rounded hover:bg-[#F0F0EB]" title="Monter"><ArrowUp size={12} /></button>
                        <span className="font-mono text-xs w-6 text-center">{it.ordre_affichage}</span>
                        <button onClick={() => move(it, 1)} className="p-1 rounded hover:bg-[#F0F0EB]" title="Descendre"><ArrowDown size={12} /></button>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#1C3A27]">{it.titre}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase text-white" style={{ backgroundColor: cat.color }}>{cat.label}</span></td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase text-white" style={{ backgroundColor: lvl.color }}>{lvl.label}</span></td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(it)}
                        data-testid={`bp-toggle-active-${it.id}`}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase ${
                          it.actif ? "bg-[#1C7A3C]/10 text-[#1C7A3C]" : "bg-[#5C6B5C]/10 text-[#5C6B5C]"
                        }`}
                      >
                        {it.actif ? <><Eye size={11} /> Actif</> : <><EyeOff size={11} /> Inactif</>}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-end">
                      <button onClick={() => openEdit(it.id)} data-testid={`bp-edit-${it.id}`} className="p-2 rounded-lg hover:bg-[#1C3A27]/5 text-[#1C3A27]" title="Modifier">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => handleDelete(it.id)} data-testid={`bp-delete-${it.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Supprimer">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <BestPracticeForm
        open={openDialog}
        onOpenChange={setOpenDialog}
        value={editing}
        onSave={handleSave}
      />
    </DashboardShell>
  );
}

function BestPracticeForm({ open, onOpenChange, value, onSave }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (value) setForm(value); }, [value]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error(`Image trop lourde (${Math.round(file.size / 1024)} Ko). Max ${Math.round(MAX_IMAGE_BYTES / 1024)} Ko.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update({ image: reader.result });
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.titre?.trim()) { toast.error("Titre obligatoire"); return; }
    setSubmitting(true);
    try {
      await onSave({
        ...form,
        mots_cles: Array.isArray(form.mots_cles)
          ? form.mots_cles
          : String(form.mots_cles || "").split(",").map((s) => s.trim()).filter(Boolean),
      });
    } finally { setSubmitting(false); }
  };

  const motsClesStr = Array.isArray(form.mots_cles) ? form.mots_cles.join(", ") : (form.mots_cles || "");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#1C3A27]">
            {form.id ? "Modifier la fiche" : "Nouvelle fiche"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label>Titre *</Label>
            <Input data-testid="bp-form-titre" value={form.titre} onChange={(e) => update({ titre: e.target.value })} placeholder="ex: Comment vacciner correctement un agneau" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>Catégorie</Label>
              <Select value={form.categorie} onValueChange={(v) => update({ categorie: v })}>
                <SelectTrigger data-testid="bp-form-categorie"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BP_CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Niveau</Label>
              <Select value={form.niveau} onValueChange={(v) => update({ niveau: v })}>
                <SelectTrigger data-testid="bp-form-niveau"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BP_LEVELS.map((l) => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Ordre d'affichage</Label>
              <Input type="number" value={form.ordre_affichage} onChange={(e) => update({ ordre_affichage: parseInt(e.target.value || 0, 10) })} />
            </div>
          </div>

          <div>
            <Label>Image (max 1.5 Mo)</Label>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[#E2E4DF] bg-white text-sm font-semibold text-[#1C3A27] hover:border-[#E05A3D]">
                <ImageIcon size={14} /> Importer une image
                <input data-testid="bp-form-image" type="file" accept="image/*" className="hidden" onChange={handleFile} />
              </label>
              {form.image && (
                <>
                  <img src={form.image} alt="" className="h-12 w-20 rounded object-cover border border-[#E2E4DF]" />
                  <button type="button" onClick={() => update({ image: "" })} className="text-xs text-red-600 underline">Retirer</button>
                </>
              )}
            </div>
          </div>

          <div>
            <Label>Résumé court</Label>
            <Textarea rows={2} value={form.resume} onChange={(e) => update({ resume: e.target.value })} placeholder="2-3 phrases qui apparaîtront sur la carte" />
          </div>

          <div>
            <Label>Contenu détaillé</Label>
            <Textarea rows={8} value={form.contenu} onChange={(e) => update({ contenu: e.target.value })} placeholder="Le contenu complet de la fiche, en plusieurs paragraphes." />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>À retenir</Label>
              <Textarea rows={4} value={form.a_retenir || ""} onChange={(e) => update({ a_retenir: e.target.value })} />
            </div>
            <div>
              <Label>Erreurs à éviter</Label>
              <Textarea rows={4} value={form.erreurs_a_eviter || ""} onChange={(e) => update({ erreurs_a_eviter: e.target.value })} />
            </div>
            <div>
              <Label>Conseils pratiques</Label>
              <Textarea rows={4} value={form.conseils_pratiques || ""} onChange={(e) => update({ conseils_pratiques: e.target.value })} />
            </div>
          </div>

          <div>
            <Label>Mots-clés (séparés par des virgules)</Label>
            <Input
              value={motsClesStr}
              onChange={(e) => update({ mots_cles: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              placeholder="vaccin, agnelage, vermifuge"
            />
          </div>

          <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#1C3A27]">
            <input type="checkbox" checked={!!form.actif} onChange={(e) => update({ actif: e.target.checked })} />
            Visible par les clients
          </label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button data-testid="bp-form-submit" onClick={submit} disabled={submitting} className="bg-[#E05A3D] hover:bg-[#C84B31]">
            {submitting ? "Enregistrement…" : (form.id ? "Mettre à jour" : "Créer la fiche")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
